import { useCallback, useEffect, useState } from "react";
import { useAdminFetch } from "../lib/admin-api";
import { Button } from "../components/ui/Button";
import { Dialog, DialogTitle, DialogDescription } from "../components/ui/Dialog";
import { Input, Label } from "../components/ui/Input";
import { useToast } from "../components/ui/Toast";

/**
 * Admin duplicate-chain review queue (migration 0075). Admin-only. Surfaces the
 * staged pairs from the nightly duplicate-parent sweep
 * (storesync.chain_duplicate_candidate) for side-by-side review, and exposes the
 * two resolutions: merge one chain into the other, or dismiss the pair.
 *
 * Detection/staging is upstream — NOTHING here auto-merges. Merge reuses the
 * exact same transactional path as the chain-detail Merge action (shared
 * mergeChainsWithinTx), so a chain merged from this queue is indistinguishable
 * from one merged on #/chains/:chain_id. Mirrors ChainDetail.tsx conventions
 * (useAdminFetch/useToast, section cards, confirm dialogs).
 */

type CandidateStatus = "pending" | "merged" | "dismissed" | "stale";

interface ChainSummary {
  chain_id: string;
  display_name: string | null;
  canonical_root: string | null;
  status: string | null;
  member_count: number;
  top_owners: string[];
  top_addresses: string[];
}

// Per-signal breakdown mirrors storesync.fn_score_chain_duplicate's jsonb.
interface Signals {
  score?: number;
  weights?: Record<string, number>;
  naming?: {
    score: number;
    root_stem_match: boolean;
    display_stem_match: boolean;
    name_token_jaccard: number;
    dba_jaccard: number;
  };
  ownership?: { score: number; shared_owner: boolean; owner_jaccard: number };
  address?: {
    score: number;
    shared_street: boolean;
    shared_city_zip: boolean;
    geo_min_distance_m: number | null;
    geo_within_100m: boolean;
  };
  digital?: {
    score: number;
    shared_website_host: boolean;
    shared_place_id: boolean;
    shared_email_domain: boolean;
    shared_phone: boolean;
  };
  cross_membership?: { score: number; shared_license_number: boolean };
}

interface Candidate {
  id: string;
  score: number;
  high_confidence: boolean;
  status: CandidateStatus;
  signals: Signals;
  merged_into_id: string | null;
  dismissed_reason: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolved_by: string | null;
  chain_a: ChainSummary;
  chain_b: ChainSummary;
}

interface ListResponse {
  status: CandidateStatus;
  limit: number;
  offset: number;
  count: number;
  candidates: Candidate[];
}

const STATUS_TABS: { key: CandidateStatus; label: string }[] = [
  { key: "pending", label: "Pending" },
  { key: "stale", label: "Stale" },
  { key: "merged", label: "Merged" },
  { key: "dismissed", label: "Dismissed" },
];

const chainLabel = (c: ChainSummary) => c.display_name || c.canonical_root || c.chain_id;

interface BulkSummary {
  min_score: number;
  executed: boolean;
  considered: number;
  merged: number;
  already_unified: number;
  skipped: number;
  licenses_reparented: number;
  plan: Array<{ survivor: string; survivor_name: string | null; source: string; source_name: string | null; source_members: number; score: number }>;
}

export default function DuplicateChains() {
  const adminFetch = useAdminFetch();
  const toast = useToast();

  const [status, setStatus] = useState<CandidateStatus>("pending");
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [mergeTarget, setMergeTarget] = useState<{ candidate: Candidate; into: ChainSummary; source: ChainSummary } | null>(null);
  const [dismissTarget, setDismissTarget] = useState<Candidate | null>(null);
  const [bulkBusy, setBulkBusy] = useState<null | "preview" | "execute">(null);
  const [bulkPlan, setBulkPlan] = useState<BulkSummary | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminFetch<ListResponse>(`/api/admin/duplicate-chains?status=${status}&limit=100`);
      setCandidates(r.candidates);
    } catch (err) {
      toast.push(`Failed to load duplicates: ${(err as Error).message}`, "error");
      setCandidates([]);
    } finally {
      setLoading(false);
    }
  }, [adminFetch, status, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const bulkMerge = useCallback(
    async (execute: boolean) => {
      setBulkBusy(execute ? "execute" : "preview");
      try {
        const r = await adminFetch<BulkSummary>(`/api/admin/duplicate-chains/bulk-merge`, {
          method: "POST",
          body: JSON.stringify({ min_score: 0.85, execute }),
        });
        setBulkPlan(r);
        if (execute) {
          toast.push(`Merged ${r.merged} pair(s); ${r.licenses_reparented} location(s) reparented.`, "success");
          await load();
        } else {
          toast.push(`Preview: ${r.merged} pair(s) would merge, ${r.already_unified} already unified, ${r.skipped} skipped.`, "success");
        }
      } catch (err) {
        toast.push(`Bulk merge failed: ${(err as Error).message}`, "error");
      } finally {
        setBulkBusy(null);
      }
    },
    [adminFetch, toast, load]
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold text-gray-900">Duplicate chains</h2>
        <p className="text-sm text-gray-600 mt-1 max-w-3xl">
          Suspected duplicate parents from the nightly sweep, high-confidence first. Review each pair
          side by side and merge one chain into the other, or dismiss the pair. Merging reuses the same
          path as the chain page — locations and aliases move to the chain you keep.
        </p>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-900">Bulk merge — high confidence (score ≥ 0.85)</h3>
            <p className="text-xs text-gray-600 mt-0.5 max-w-2xl">
              Clears the high-confidence backlog in one pass. Survivor = most locations, then best name
              (mixed-case retail name over ALL-CAPS legal name). <b>Preview</b> writes nothing; <b>Execute</b>
              performs the merges (same reversible path as a single merge — locations/aliases move, the other
              chain is retired with a merged-into record). Preview before you execute.
            </p>
          </div>
          <div className="flex gap-2 flex-shrink-0">
            <Button variant="secondary" disabled={bulkBusy !== null} onClick={() => bulkMerge(false)}>
              {bulkBusy === "preview" ? "Previewing…" : "Preview"}
            </Button>
            <Button
              variant="primary"
              disabled={bulkBusy !== null || !bulkPlan || bulkPlan.executed || bulkPlan.merged === 0}
              onClick={() => bulkMerge(true)}
            >
              {bulkBusy === "execute" ? "Merging…" : "Execute"}
            </Button>
          </div>
        </div>
        {bulkPlan && (
          <div className="text-xs text-gray-600 border-t border-gray-100 pt-2">
            {bulkPlan.executed ? "Merged" : "Would merge"} <b>{bulkPlan.merged}</b> pair(s), reparenting{" "}
            <b>{bulkPlan.licenses_reparented}</b> location(s); {bulkPlan.already_unified} already unified,{" "}
            {bulkPlan.skipped} skipped, of {bulkPlan.considered} considered.
            {bulkPlan.plan.length > 0 && (
              <ul className="mt-2 max-h-56 overflow-auto rounded-md border border-gray-100 divide-y divide-gray-100">
                {bulkPlan.plan.map((p, i) => (
                  <li key={i} className="px-2 py-1">
                    <span className="text-gray-700">{p.source_name || p.source}</span>
                    <span className="text-gray-400"> → </span>
                    <span className="font-medium text-gray-900">{p.survivor_name || p.survivor}</span>
                    <span className="text-gray-400">
                      {" "}
                      ({p.source_members} loc · {p.score.toFixed(2)})
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 border-b border-gray-200">
        {STATUS_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setStatus(t.key)}
            className={`relative px-3 py-2 text-sm font-medium transition-colors ${
              status === t.key ? "text-accent-700" : "text-gray-500 hover:text-gray-800"
            }`}
          >
            {t.label}
            {status === t.key && <span className="absolute inset-x-2 -bottom-px h-0.5 bg-accent-500 rounded-full" />}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : candidates.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-10 text-center text-sm text-gray-500">
          No {status} duplicate pairs.
        </div>
      ) : (
        <div className="space-y-5">
          {candidates.map((c) => (
            <CandidateCard
              key={c.id}
              candidate={c}
              onMerge={(into, source) => setMergeTarget({ candidate: c, into, source })}
              onDismiss={() => setDismissTarget(c)}
            />
          ))}
        </div>
      )}

      {mergeTarget && (
        <MergeDialog
          candidate={mergeTarget.candidate}
          into={mergeTarget.into}
          source={mergeTarget.source}
          adminFetch={adminFetch}
          onClose={() => setMergeTarget(null)}
          onDone={async () => { setMergeTarget(null); await load(); }}
        />
      )}
      {dismissTarget && (
        <DismissDialog
          candidate={dismissTarget}
          adminFetch={adminFetch}
          onClose={() => setDismissTarget(null)}
          onDone={async () => { setDismissTarget(null); await load(); }}
        />
      )}
    </div>
  );
}

type AdminFetch = ReturnType<typeof useAdminFetch>;

function CandidateCard({
  candidate, onMerge, onDismiss,
}: {
  candidate: Candidate;
  onMerge: (into: ChainSummary, source: ChainSummary) => void;
  onDismiss: () => void;
}) {
  const [showSignals, setShowSignals] = useState(false);
  const { chain_a, chain_b, score, high_confidence, status } = candidate;
  const pct = Math.round(score * 100);
  const resolved = status !== "pending" && status !== "stale";

  return (
    <section className="rounded-lg border border-gray-200 bg-white shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-3">
        <div className="flex items-center gap-3">
          <span className="text-sm font-semibold text-gray-900">Score {score.toFixed(3)}</span>
          {high_confidence && (
            <span className="inline-flex items-center rounded-full bg-red-50 text-red-700 border border-red-100 px-2 py-0.5 text-xs font-medium">
              High confidence
            </span>
          )}
          <StatusBadge status={status} />
        </div>
        <div className="w-40">
          <div className="h-2 w-full rounded-full bg-gray-100 overflow-hidden">
            <div
              className={`h-full rounded-full ${high_confidence ? "bg-red-500" : "bg-accent-500"}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
        <ChainPane summary={chain_a} label="A" />
        <ChainPane summary={chain_b} label="B" />
      </div>

      <div className="border-t border-gray-100 px-5 py-3">
        <button
          type="button"
          className="text-xs font-medium text-gray-500 hover:text-gray-800"
          onClick={() => setShowSignals((v) => !v)}
        >
          {showSignals ? "▾ Hide" : "▸ Show"} signal breakdown
        </button>
        {showSignals && <SignalBreakdown signals={candidate.signals} />}
      </div>

      {resolved ? (
        <div className="border-t border-gray-100 bg-gray-50/60 px-5 py-3 text-xs text-gray-500">
          {status === "merged" && candidate.merged_into_id && (
            <span>
              Merged into <span className="font-mono">{candidate.merged_into_id}</span>
              {candidate.resolved_by ? ` by ${candidate.resolved_by}` : ""}.
            </span>
          )}
          {status === "dismissed" && (
            <span>
              Dismissed{candidate.dismissed_reason ? `: "${candidate.dismissed_reason}"` : ""}
              {candidate.resolved_by ? ` by ${candidate.resolved_by}` : ""}.
            </span>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-gray-100 px-5 py-3">
          <Button variant="ghost" onClick={onDismiss}>Dismiss</Button>
          <Button variant="secondary" onClick={() => onMerge(chain_a, chain_b)}>
            Merge into A ({chainLabel(chain_a)})
          </Button>
          <Button variant="secondary" onClick={() => onMerge(chain_b, chain_a)}>
            Merge into B ({chainLabel(chain_b)})
          </Button>
        </div>
      )}
    </section>
  );
}

function StatusBadge({ status }: { status: CandidateStatus }) {
  const map: Record<CandidateStatus, string> = {
    pending: "bg-amber-50 text-amber-700 border-amber-100",
    stale: "bg-gray-50 text-gray-500 border-gray-200",
    merged: "bg-green-50 text-green-700 border-green-100",
    dismissed: "bg-gray-50 text-gray-500 border-gray-200",
  };
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${map[status]}`}>
      {status}
    </span>
  );
}

function ChainPane({ summary, label }: { summary: ChainSummary; label: string }) {
  return (
    <div className="px-5 py-4">
      <div className="flex items-baseline justify-between gap-2">
        <a
          href={`#/chains/${encodeURIComponent(summary.chain_id)}`}
          className="text-sm font-semibold text-gray-900 hover:text-accent-700 truncate"
        >
          {chainLabel(summary)}
        </a>
        <span className="shrink-0 text-xs text-gray-400">Chain {label}</span>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        <span className="font-mono">{summary.canonical_root}</span>
        {" · "}
        <span className={summary.status === "active" ? "text-green-700" : "text-gray-500"}>{summary.status}</span>
        {" · "}
        {summary.member_count} location{summary.member_count === 1 ? "" : "s"}
      </p>
      <dl className="mt-3 space-y-2">
        <FactRow label="Top owners" values={summary.top_owners} />
        <FactRow label="Top addresses" values={summary.top_addresses} />
      </dl>
    </div>
  );
}

function FactRow({ label, values }: { label: string; values: string[] }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wide text-gray-400">{label}</dt>
      <dd className="mt-0.5 text-xs text-gray-700">
        {values && values.length > 0 ? (
          <ul className="space-y-0.5">
            {values.map((v, i) => (
              <li key={i} className="truncate">{v}</li>
            ))}
          </ul>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </dd>
    </div>
  );
}

function SignalBreakdown({ signals }: { signals: Signals }) {
  const { naming, ownership, address, digital, cross_membership, weights } = signals;
  const w = (k: string) => (weights && typeof weights[k] === "number" ? weights[k] : undefined);
  return (
    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      <SignalGroup title="Naming" weight={w("naming")} score={naming?.score}
        flags={[
          ["Root stem match", naming?.root_stem_match],
          ["Display stem match", naming?.display_stem_match],
        ]}
        metrics={[
          ["Name token Jaccard", naming?.name_token_jaccard],
          ["DBA Jaccard", naming?.dba_jaccard],
        ]}
      />
      <SignalGroup title="Ownership" weight={w("ownership")} score={ownership?.score}
        flags={[["Shared owner", ownership?.shared_owner]]}
        metrics={[["Owner Jaccard", ownership?.owner_jaccard]]}
      />
      <SignalGroup title="Address" weight={w("address")} score={address?.score}
        flags={[
          ["Shared street", address?.shared_street],
          ["Shared city+zip", address?.shared_city_zip],
          ["Within 100m", address?.geo_within_100m],
        ]}
        metrics={[["Min distance (m)", address?.geo_min_distance_m ?? null]]}
      />
      <SignalGroup title="Digital" weight={w("digital")} score={digital?.score}
        flags={[
          ["Shared website host", digital?.shared_website_host],
          ["Shared GBP place_id", digital?.shared_place_id],
          ["Shared email domain", digital?.shared_email_domain],
          ["Shared phone", digital?.shared_phone],
        ]}
      />
      <SignalGroup title="Cross-membership" weight={w("cross_membership")} score={cross_membership?.score}
        flags={[["Shared license #", cross_membership?.shared_license_number]]}
      />
    </div>
  );
}

function SignalGroup({
  title, weight, score, flags = [], metrics = [],
}: {
  title: string;
  weight?: number;
  score?: number;
  flags?: [string, boolean | undefined][];
  metrics?: [string, number | null | undefined][];
}) {
  return (
    <div className="rounded-md border border-gray-100 bg-gray-50/50 p-3">
      <div className="flex items-baseline justify-between">
        <span className="text-xs font-semibold text-gray-800">{title}</span>
        <span className="text-[11px] text-gray-500">
          {typeof score === "number" ? score.toFixed(3) : "—"}
          {typeof weight === "number" ? ` / ${weight.toFixed(2)}` : ""}
        </span>
      </div>
      <ul className="mt-2 space-y-1">
        {flags.map(([label, on]) => (
          <li key={label} className="flex items-center justify-between text-xs">
            <span className="text-gray-600">{label}</span>
            <span className={on ? "text-green-700 font-medium" : "text-gray-300"}>{on ? "yes" : "no"}</span>
          </li>
        ))}
        {metrics.map(([label, val]) => (
          <li key={label} className="flex items-center justify-between text-xs">
            <span className="text-gray-600">{label}</span>
            <span className="text-gray-700 font-mono">{val === null || val === undefined ? "—" : val}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function MergeDialog({
  candidate, into, source, adminFetch, onClose, onDone,
}: {
  candidate: Candidate;
  into: ChainSummary;
  source: ChainSummary;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await adminFetch<{ licenses_reparented: number }>(
        `/api/admin/duplicate-chains/${candidate.id}/merge`,
        { method: "POST", body: JSON.stringify({ into_chain_id: into.chain_id }) }
      );
      toast.push(
        `Merged "${chainLabel(source)}" into "${chainLabel(into)}" — ${r.licenses_reparented} location(s) moved.`,
        "success"
      );
      await onDone();
    } catch (err) {
      toast.push(`Merge failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Merge duplicate chains</DialogTitle>
      <DialogDescription>
        "{chainLabel(source)}" ({source.member_count} location{source.member_count === 1 ? "" : "s"}) will be
        merged into "{chainLabel(into)}". Its locations and aliases move to the destination and the source
        chain is marked merged. This cannot be undone from the UI.
      </DialogDescription>
      <div className="mt-5 rounded-md border border-accent-200 bg-accent-50/40 p-3 text-sm text-gray-700">
        Merge <span className="font-medium">{chainLabel(source)}</span> ({source.member_count})
        {" → "}
        <span className="font-medium">{chainLabel(into)}</span> ({into.member_count})
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Merging…" : "Confirm merge"}</Button>
      </div>
    </Dialog>
  );
}

function DismissDialog({
  candidate, adminFetch, onClose, onDone,
}: {
  candidate: Candidate;
  adminFetch: AdminFetch;
  onClose: () => void;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await adminFetch(`/api/admin/duplicate-chains/${candidate.id}/dismiss`, {
        method: "POST",
        body: JSON.stringify({ reason: reason.trim() || undefined }),
      });
      toast.push("Pair dismissed.", "success");
      await onDone();
    } catch (err) {
      toast.push(`Dismiss failed: ${(err as Error).message}`, "error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogTitle>Dismiss this pair</DialogTitle>
      <DialogDescription>
        Mark "{chainLabel(candidate.chain_a)}" and "{chainLabel(candidate.chain_b)}" as not duplicates. The
        nightly sweep won't resurface this pair. You can optionally note why.
      </DialogDescription>
      <div className="mt-5">
        <Label htmlFor="dismiss-reason" hint="(optional)">Reason</Label>
        <Input
          id="dismiss-reason"
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
          placeholder="e.g. different owners, coincidental name overlap"
        />
      </div>
      <div className="mt-6 flex justify-end gap-2">
        <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="primary" onClick={submit} disabled={busy}>{busy ? "Dismissing…" : "Dismiss pair"}</Button>
      </div>
    </Dialog>
  );
}
